import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface CalendarTask {
    id: string | number;
    title: string;
    start: string;
    end: string;
    comment?: string;
    type_id?: number;
    extendedProps?: {
        item: any;
    };
    [key: string]: any;
}

interface CalendarDates {
    filterStart: string;
    filterEnd: string;
    start: string;
    end: string;
    year: string;
    monthStart: string;
    monthEnd: string;
    dayStart: string;
    dayEnd: string;
    origin: string;
}

interface CalendarState {
    tasks: CalendarTask[];
    loading: boolean;
    dates: CalendarDates;
    selectedViewType: string;
    prevSelectedViewType: string;
}

// Восстанавливаем состояние из localStorage при инициализации
const getInitialState = (): CalendarState => {
    const stored = localStorage.getItem('calendarState');
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            return {
                ...parsed,
                loading: false, // При восстановлении не показываем загрузку
            };
        } catch (e) {
            console.error('Failed to parse calendar state from localStorage', e);
        }
    }
    
    return {
        tasks: [],
        loading: true,
        dates: {
            filterStart: '',
            filterEnd: '',
            start: '',
            end: '',
            year: '',
            monthStart: '',
            monthEnd: '',
            dayStart: '',
            dayEnd: '',
            origin: '',
        },
        selectedViewType: '',
        prevSelectedViewType: '',
    };
};

const initialState: CalendarState = getInitialState();

const calendarSlice = createSlice({
    name: 'calendar',
    initialState,
    reducers: {
        setTasks(state, action: PayloadAction<CalendarTask[]>) {
            state.tasks = action.payload;
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        addTask(state, action: PayloadAction<CalendarTask>) {
            const existingIndex = state.tasks.findIndex(
                t => t.id === action.payload.id
            );
            if (existingIndex >= 0) {
                state.tasks[existingIndex] = action.payload;
            } else {
                state.tasks.push(action.payload);
            }
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        updateTask(state, action: PayloadAction<{ id: string | number; updates: Partial<CalendarTask> }>) {
            const task = state.tasks.find(t => t.id === action.payload.id);
            if (task) {
                Object.assign(task, action.payload.updates);
            }
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        removeTask(state, action: PayloadAction<string | number>) {
            state.tasks = state.tasks.filter(t => t.id !== action.payload);
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        setDates(state, action: PayloadAction<Partial<CalendarDates>>) {
            state.dates = { ...state.dates, ...action.payload };
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        setSelectedViewType(state, action: PayloadAction<string>) {
            state.prevSelectedViewType = state.selectedViewType;
            state.selectedViewType = action.payload;
            // Сохраняем в localStorage
            localStorage.setItem('calendarState', JSON.stringify(state));
        },
        clearCalendar(state) {
            state.tasks = [];
            state.loading = true;
            state.dates = {
                filterStart: '',
                filterEnd: '',
                start: '',
                end: '',
                year: '',
                monthStart: '',
                monthEnd: '',
                dayStart: '',
                dayEnd: '',
                origin: '',
            };
            // Очищаем localStorage
            localStorage.removeItem('calendarState');
        },
    },
});

export const {
    setTasks,
    addTask,
    updateTask,
    removeTask,
    setLoading,
    setDates,
    setSelectedViewType,
    clearCalendar,
} = calendarSlice.actions;

export default calendarSlice.reducer;

